<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         // Default tab mengikuti triwulan yang sedang berjalan (real time):
         // Jan-Mar -> Triwulan I, Apr-Jun -> II, Jul-Sep -> III, Okt-Des -> IV.
         activeTab: Math.floor(new Date().getMonth() / 3),
         loading: false,
      }
   },
   computed: {
      ...mapState({
         demografi: state => state.DashboardSkm.demografi,
         tahun: state => state.DashboardSkm.tahun,
         refreshTimestamp: state => state.DashboardSkm.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      activeTw() {
         return this.activeTab + 1
      },
      jkSeries() {
         return [
            Number(this.demografi.jk.l) || 0,
            Number(this.demografi.jk.p) || 0,
         ]
      },
      usiaCategories() {
         return Object.keys(this.demografi.usia || {})
      },
      usiaSeries() {
         return [{
            name: 'Responden',
            data: Object.values(this.demografi.usia || {}).map(value => Number(value) || 0),
         }]
      },
      pendidikanCategories() {
         return Object.keys(this.demografi.pendidikan || {})
      },
      pendidikanSeries() {
         return [{
            name: 'Responden',
            data: Object.values(this.demografi.pendidikan || {}).map(value => Number(value) || 0),
         }]
      },
      pekerjaanCategories() {
         return Object.keys(this.demografi.pekerjaan || {})
      },
      pekerjaanSeries() {
         return [{
            name: 'Responden',
            data: Object.values(this.demografi.pekerjaan || {}).map(value => Number(value) || 0),
         }]
      },
      jkOptions() {
         const textColor = this.isDark ? '#d0d2d6' : '#5e5873'
         const bgColor = this.isDark ? '#283046' : '#ffffff'

         return {
            chart: {
               type: 'donut',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            labels: ['Laki-laki', 'Perempuan'],
            colors: ['#7367f0', '#ea5455'],
            dataLabels: {
               enabled: false,
            },
            stroke: {
               show: true,
               curve: 'smooth',
               lineCap: 'round',
               colors: [bgColor],
               width: 6,
            },
            plotOptions: {
               pie: {
                  donut: {
                     size: '65%',
                     labels: {
                        show: true,
                        name: {
                           show: true,
                           fontSize: '14px',
                           color: textColor,
                        },
                        value: {
                           show: true,
                           fontSize: '22px',
                           fontWeight: 700,
                           color: textColor,
                           formatter: value => Number(value).toLocaleString('id-ID'),
                        },
                        total: {
                           show: true,
                           showAlways: true,
                           label: 'Total',
                           fontSize: '14px',
                           color: this.isDark ? '#b4b7bd' : '#6e6b7b',
                           formatter: w => Number(w.globals.seriesTotals.reduce((a, b) => a + b, 0)).toLocaleString('id-ID'),
                        },
                     },
                  },
               },
            },
            legend: {
               position: 'bottom',
               labels: {
                  colors: textColor,
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${Number(value).toLocaleString('id-ID')} orang`,
               },
            },
         }
      },
   },
   watch: {
      tahun(newYear, oldYear) {
         if (newYear !== oldYear) this.loadData({ year: newYear, tw: this.activeTw })
      },
      activeTab() {
         this.loadData({ year: this.tahun, tw: this.activeTw })
      },
      refreshTimestamp() {
         this.loadData({ year: this.tahun, tw: this.activeTw })
      },
   },
   mounted() {
      this.loadData({ year: this.tahun, tw: this.activeTw })
   },
   methods: {
      ...mapActions({
         getDemografi: 'DashboardSkm/getDemografi',
      }),
      async loadData(params) {
         this.loading = true
         try {
            await this.getDemografi(params)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      barOptions(categories, color, title) {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: [color],
            plotOptions: {
               bar: {
                  borderRadius: 4,
                  columnWidth: '45%',
               },
            },
            dataLabels: {
               enabled: true,
               formatter: value => Number(value).toLocaleString('id-ID'),
               style: {
                  colors: [textColor],
               },
            },
            grid: {
               borderColor: this.isDark ? '#3b4253' : '#ebe9f1',
               strokeDashArray: 4,
            },
            xaxis: {
               categories,
               labels: {
                  style: {
                     colors: textColor,
                  },
                  rotate: categories.length > 6 ? -30 : 0,
               },
               title: {
                  text: title,
                  style: {
                     color: textColor,
                  },
               },
            },
            yaxis: {
               min: 0,
               forceNiceScale: true,
               labels: {
                  style: {
                     colors: [textColor],
                  },
                  formatter: value => Math.round(value),
               },
            },
            legend: {
               show: false,
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${Number(value).toLocaleString('id-ID')} responden`,
               },
            },
         }
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex justify-content-between align-items-center mb-1">
                  <div>
                     <h4 class="mb-25">
                        Demografi Responden
                     </h4>
                     <small class="text-muted">Profil responden SKM Tahun {{ tahun }}</small>
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab title="Triwulan I" />
                     <b-tab title="Triwulan II" />
                     <b-tab title="Triwulan III" />
                     <b-tab title="Triwulan IV" />
                  </b-tabs>
               </div>

               <b-row>
                  <b-col cols="12" md="6" class="mb-1">
                     <h5 class="chart-title">
                        Jenis Kelamin
                     </h5>
                     <vue-apex-charts
                        type="donut"
                        height="300"
                        :options="jkOptions"
                        :series="jkSeries"
                     />
                  </b-col>
                  <b-col cols="12" md="6" class="mb-1">
                     <h5 class="chart-title">
                        Kelompok Usia
                     </h5>
                     <vue-apex-charts
                        type="bar"
                        height="300"
                        :options="barOptions(usiaCategories, '#00cfe8', 'Kelompok Usia')"
                        :series="usiaSeries"
                     />
                  </b-col>
                  <b-col cols="12" md="6" class="mb-1">
                     <h5 class="chart-title">
                        Tingkat Pendidikan
                     </h5>
                     <vue-apex-charts
                        type="bar"
                        height="300"
                        :options="barOptions(pendidikanCategories, '#ff9f43', 'Tingkat Pendidikan')"
                        :series="pendidikanSeries"
                     />
                  </b-col>
                  <b-col cols="12" md="6" class="mb-1">
                     <h5 class="chart-title">
                        Jenis Pekerjaan
                     </h5>
                     <vue-apex-charts
                        type="bar"
                        height="300"
                        :options="barOptions(pekerjaanCategories, '#7367f0', 'Jenis Pekerjaan')"
                        :series="pekerjaanSeries"
                     />
                  </b-col>
               </b-row>
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.chart-title {
   font-weight: 600;
   margin-bottom: 0.5rem;
}
</style>
